/**
 * API client for the order regimen / ICD / auth-status endpoint.
 *
 * Wraps `POST /backend-service/medical/order/regimen-icd`, which returns an
 * order's regimen name, ICD-10 codes, and auth status (PG-primary with a
 * Firestore fallback). Attaches the in-memory bearer token for authorization.
 * @module orderRegimenIcd
 */
import { getBearerToken } from "@risalabs_frontend_org/auth-wrapper";
import {
  OrderRegimenIcdRequest,
  OrderRegimenIcdResponse,
} from "../../data-model/orderRegimenIcd";
import { API_ENDPOINTS } from "../../enums/apiUrls";
import { logError } from "../../utils/customLogger";

/**
 * Fetches an order's regimen name, ICD codes, and auth status.
 *
 * @param payload - Order ID and optional org ID for RLS-scoped reads
 * @returns The order's regimen name, ICD codes, and auth statuses
 * @throws {Error} When the token is missing or the response is non-OK
 */
export const getOrderRegimenIcd = async (
  payload: OrderRegimenIcdRequest,
): Promise<OrderRegimenIcdResponse> => {
  try {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Bearer token not available");
    }

    const response = await fetch(API_ENDPOINTS.ORDER_REGIMEN_ICD, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return (await response.json()) as OrderRegimenIcdResponse;
  } catch (error) {
    logError(error as Error, "Error fetching order regimen/ICD");
    throw error;
  }
};
